// Ilovaning yagona kirish oqimi (Q-021):
//   restoran logini -> (yangi qurilma: ega/menejer tasdiqlaydi) -> PIN -> (2-parol) -> ish oynasi.
// Ish oynasi qurilma turi va xodimga qarab: kassa, oshxona ekrani yoki boshqaruv paneli.
import { Suspense, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { BrandMark, Button, Spinner, Toaster, useApplyUi } from '@onepos/ui';
import './kassa.css';
import { KassaProvider, useKassa } from './store';
import { LockSign } from './LockSign';
import { PinScreen } from './PinScreen';
import { lazyNamed } from '../lib/lazy';
import { WaiterBills } from './waiterBills';
import { LoginPage } from '../pages/LoginPage';

// Kassa va oshxona ekrani — alohida bo'laklar (Q-073): oshxona planshetiga kassa kodi kerak emas
const PosScreen = lazyNamed(() => import('./PosScreen'), 'PosScreen');
const KdsScreen = lazyNamed(() => import('./KdsScreen'), 'KdsScreen');
// Demo sayt (demo.onepos.uz): faqat `--mode demo` build'ida qo'shiladi
const DemoStart = import.meta.env.MODE === 'demo' ? lazyNamed(() => import('../demo/DemoLayer'), 'DemoStart') : null;
const DemoBadge = import.meta.env.MODE === 'demo' ? lazyNamed(() => import('../demo/DemoLayer'), 'DemoBadge') : null;

export function Root({ panel }: { panel: ReactNode }) {
  return (
    <KassaProvider>
      <Gate panel={panel} />
      <WaiterBills />
      {DemoBadge && (
        <Suspense fallback={null}>
          <DemoBadge />
        </Suspense>
      )}
    </KassaProvider>
  );
}

function Gate({ panel }: { panel: ReactNode }) {
  const { phase, staff, message, boot } = useKassa();
  const navigate = useNavigate();

  // Xodim endi kirdi: boshqaruv qurilmasida — panel, aks holda — kassa/oshxona
  const prev = useRef(staff?.id ?? null);
  useEffect(() => {
    const was = prev.current;
    prev.current = staff?.id ?? null;
    if (!staff || was === staff.id) return;
    navigate(boot?.terminal.kind === 'manager' && staff.panel && !staff.offline ? '/' : '/kassa', { replace: true });
  }, [staff, boot, navigate]);

  if (phase === 'loading') return <BootSpinner />;
  if (phase === 'unpaired')
    return (
      <>
        <RestaurantUi />
        {DemoStart ? (
          <Suspense fallback={<BootSpinner />}>
            <DemoStart />
          </Suspense>
        ) : (
          <LoginPage />
        )}
      </>
    );
  if (phase === 'need_online') {
    return (
      <div className="ks-lock">
        <RestaurantUi />
        <div className="ks-lock__box">
          <h1 className="ks-lock__title">Internet kerak</h1>
          <p className="ks-lock__sub">Qurilma birinchi marta menyuni yuklab olishi uchun internetga ulaning. Keyin internetsiz ham ishlaydi.</p>
          {message && <p className="ks-err">{message}</p>}
          <Button variant="primary" className="ks-wide" onClick={() => window.location.reload()}>
            Qayta urinish
          </Button>
        </div>
        <LockSign />
      </div>
    );
  }
  if (!staff || phase === 'pending')
    return (
      <>
        <RestaurantUi />
        <PinScreen />
      </>
    );

  return (
    <Routes>
      <Route
        path="/kassa/*"
        element={
          <>
            <Suspense fallback={<BootSpinner />}>
              {boot && !TENANT_WORKING.includes(boot.tenant.status) ? <StoppedScreen /> : boot?.terminal.kind === 'kds' ? <KdsScreen /> : <PosScreen />}
            </Suspense>
            <Toaster />
          </>
        }
      />
      <Route path="*" element={<PanelGate panel={panel} />} />
    </Routes>
  );
}

const TENANT_WORKING = ['trial', 'active', 'grace'];

function BootSpinner() {
  return (
    <div className="ks-lock">
      <RestaurantUi />
      <div className="ks-boot">
        <BrandMark size={72} tone="side" title="ONE POS" />
        <Spinner label="Yuklanmoqda…" />
      </div>
    </div>
  );
}

/** Qulf / PIN / kutish oynalari — restoran standarti ko'rinishida (Q-031) */
function RestaurantUi() {
  const { boot } = useKassa();
  useApplyUi({ personal: 'auto', restaurant: boot?.settings['ui.theme'], accent: boot?.settings['ui.accent'] });
  return null;
}

/** Obuna to'xtatilgan: savdo yopiq (internetsiz ham), tarixni panelda ko'rish mumkin (Q-022, Q-024) */
function StoppedScreen() {
  const { staff, lock } = useKassa();
  const navigate = useNavigate();
  return (
    <div className="ks-lock">
      <RestaurantUi />
      <div className="ks-lock__box">
        <h1 className="ks-lock__title">Restoran to‘xtatilgan</h1>
        <p className="ks-lock__sub">
          Obuna to‘xtatilgani uchun savdo yopiq. Ma’lumotlar saqlanadi — tarix va hisobotlarni boshqaruv panelida ko‘rish mumkin. Obunani
          tiklash uchun ONE POS bilan bog‘laning.
        </p>
        {staff?.panel && !staff.offline && (
          <Button variant="primary" className="ks-wide" onClick={() => navigate('/')}>
            Boshqaruv paneli
          </Button>
        )}
        <Button className="ks-wide" onClick={() => lock()}>
          Qulflash
        </Button>
      </div>
      <LockSign />
    </div>
  );
}

function PanelGate({ panel }: { panel: ReactNode }) {
  const { staff, lock } = useKassa();
  if (!staff?.panel) return <Navigate to="/kassa" replace />;
  if (staff.offline || !staff.session) {
    return (
      <div className="ks-lock">
        <RestaurantUi />
        <div className="ks-lock__box">
          <h1 className="ks-lock__title">Boshqaruv uchun internet kerak</h1>
          <p className="ks-lock__sub">Siz internetsiz kirdingiz. Savdo ishlaydi; hisobot va sozlamalar internet qaytgach ochiladi.</p>
          <Button variant="primary" className="ks-wide" onClick={() => window.location.assign('/kassa')}>
            Kassaga o‘tish
          </Button>
          <Button className="ks-wide" onClick={() => lock()}>
            Chiqish
          </Button>
        </div>
        <LockSign />
      </div>
    );
  }
  return <>{panel}</>;
}

